import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw } from "lucide-react";
import { api, BACKEND_URL, formatApiError } from "@/lib/api";

const KINDS = ["", "contact", "volunteer", "partnership", "sponsor", "community_support",
    "volunteer_application", "corporate_partnership", "goods_donation", "donation_pledge",
    "ecocash_donation", "donation_confirmation",
    "skills_register", "support_request", "newsletter", "skills_registration", "media_enquiry"];
const STATUSES = ["", "new", "reviewed", "actioned", "archived"];

export default function AdminSubmissions() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [kind, setKind] = useState("");
    const [status, setStatus] = useState("");
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (kind) params.set("kind", kind);
            if (status) params.set("status", status);
            const { data } = await api.get(`/admin/submissions?${params.toString()}`);
            setItems(data.items);
            setTotal(data.total);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    }, [kind, status]);

    useEffect(() => { load(); }, [load]);

    async function updateStatus(id, newStatus) {
        try {
            await api.patch(`/admin/submissions/${id}`, { status: newStatus });
            toast.success("Status updated");
            load();
            if (selected?.id === id) setSelected({ ...selected, status: newStatus });
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function retryQueue() {
        try {
            const { data } = await api.post("/admin/email/retry-queue");
            toast.success(`Processed ${data.processed}, sent ${data.sent}`);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div data-testid="admin-submissions">
            <div className="flex justify-between items-end flex-wrap gap-3">
                <div>
                    <div className="gsf-eyebrow">Inbox</div>
                    <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Submissions ({total})</h1>
                </div>
                <div className="flex gap-2">
                    <button onClick={retryQueue} className="gsf-btn-outline text-sm" data-testid="retry-queue-btn"><RefreshCw size={14} /> Retry email queue</button>
                    <a href={`${BACKEND_URL}/api/admin/submissions/export.csv${kind ? `?kind=${kind}` : ""}`} className="gsf-btn-outline text-sm" data-testid="export-csv-btn">
                        <Download size={14} /> Export CSV
                    </a>
                </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
                <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="filter-kind"
                    className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white">
                    {KINDS.map((k) => <option key={k} value={k}>{k ? k.replace(/_/g, " ") : "All forms"}</option>)}
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status"
                    className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white">
                    {STATUSES.map((k) => <option key={k} value={k}>{k || "All statuses"}</option>)}
                </select>
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-3 gsf-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-[#f8faf7] text-left">
                            <tr>
                                <th className="p-3">Kind</th>
                                <th className="p-3">Received</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Email</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={4} className="p-6 text-gray-500 text-center">Loading…</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={4} className="p-6 text-gray-500 text-center">No submissions.</td></tr>
                            ) : items.map((it) => (
                                <tr key={it.id} onClick={() => setSelected(it)} data-testid={`row-${it.id}`}
                                    className={`border-t border-gray-100 cursor-pointer hover:bg-[color:var(--gsf-soft-green)]/50 ${selected?.id === it.id ? "bg-[color:var(--gsf-soft-green)]" : ""}`}>
                                    <td className="p-3 capitalize font-medium">{it.kind.replace(/_/g, " ")}</td>
                                    <td className="p-3 text-gray-500">{new Date(it.created_at).toLocaleString()}</td>
                                    <td className="p-3"><StatusBadge status={it.status} /></td>
                                    <td className="p-3"><EmailBadge status={it.email_dispatch_status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="lg:col-span-2">
                    {selected ? (
                        <div className="gsf-card p-6 sticky top-6" data-testid="submission-detail">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="gsf-eyebrow">{selected.kind.replace(/_/g, " ")}</div>
                                    <h3 className="mt-1 font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">Submission</h3>
                                </div>
                                <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-sm" data-testid="detail-close">✕</button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{new Date(selected.created_at).toLocaleString()}</p>
                            <div className="mt-4 space-y-2 max-h-96 overflow-y-auto text-sm">
                                {Object.entries(selected.fields || {}).map(([k, v]) => (
                                    <div key={k} className="grid grid-cols-3 gap-3">
                                        <div className="text-gray-500 capitalize col-span-1">{k.replace(/_/g, " ")}</div>
                                        <div className="col-span-2 break-words">{String(v || "—")}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 border-t border-gray-100 pt-4">
                                <div className="text-xs text-gray-500 mb-2">Email dispatch: <EmailBadge status={selected.email_dispatch_status} /></div>
                                <div className="text-xs text-gray-500 mb-2">Status:</div>
                                <div className="flex flex-wrap gap-2">
                                    {["new", "reviewed", "actioned", "archived"].map((s) => (
                                        <button key={s} onClick={() => updateStatus(selected.id, s)} data-testid={`set-status-${s}`}
                                            className={`text-xs px-3 py-1.5 rounded-full ${selected.status === s ? "bg-[color:var(--gsf-green-700)] text-white" : "bg-[color:var(--gsf-soft-green)] text-[color:var(--gsf-green-700)]"}`}>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="gsf-card p-8 text-center text-gray-500 text-sm">Select a submission to review details.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = { new: "bg-blue-50 text-blue-700", reviewed: "bg-amber-50 text-amber-700", actioned: "bg-green-50 text-green-700", archived: "bg-gray-100 text-gray-600" };
    return <span className={`text-[11px] px-2 py-1 rounded-full ${map[status] || "bg-gray-100"}`}>{status}</span>;
}
function EmailBadge({ status }) {
    const map = { sent: "bg-green-50 text-green-700", pending: "bg-blue-50 text-blue-700", failed: "bg-red-50 text-red-700", disabled: "bg-amber-50 text-amber-700" };
    return <span className={`text-[11px] px-2 py-1 rounded-full ${map[status] || "bg-gray-100"}`}>{status}</span>;
}
